import { useEffect, useState } from "react";
import {
  languagesDataType,
  VacancyDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import RichTextEditor from "react-rte";
import { VacanciesService } from "../../services/vacancy.service";
import ShowImage from "../../components/shared/show-image/ShowImage";
import SelectImage from "../../components/shared/select-image/SelectImage";
import { FaPlus } from "react-icons/fa";
const Vacancy = () => {
  const [vacancy, setVacancy] = useState<VacancyDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<VacancyDataType | null>(
    null
  );
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState("AZ");
  const [logoFileList, setLogoFileList] = useState<any[]>([]);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");

  const [editorState, setEditorState] = useState<{ [key: string]: any }>({
    AZ: RichTextEditor.createEmptyValue(),
  });
  const handleEditorChange = (value: any) => {
    setEditorState((prev) => ({
      ...prev,
      [langCode]: value,
    }));

    const textValue = value.toString("html");

    handleTranslationChange(textValue, "description");
  };

  useEffect(() => {
    if (isEditing) {
      const initialEditorState: { [key: string]: any } = {
        AZ: editingRecord?.description
          ? RichTextEditor.createValueFromString(
              editingRecord?.description,
              "html"
            )
          : RichTextEditor.createEmptyValue(),
      };
      editingRecord?.translations?.forEach((translation) => {
        initialEditorState[translation.lang_code] = translation.description
          ? RichTextEditor.createValueFromString(
              translation.description,
              "html"
            )
          : RichTextEditor.createEmptyValue();
      });

      setEditorState(initialEditorState);
    }
  }, [editingRecord, isEditing]);

  useEffect(() => {
    setLoading(true);
    VacanciesService.getAllVacancies()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: VacancyDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setVacancy(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);
  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await VacanciesService.getAllVacancies();
    setVacancy(
      updatedData?.data?.data?.map((item: VacancyDataType, index: number) => ({
        ...item,
        key: index + 1,
      }))
    );
  };
  const { handleSubmit, values, resetForm, setFieldValue, touched, errors } =
    useFormik({
      initialValues: {
        title: "",
        description: "",
        image: null,
        translations: [] as {
          lang_code: string;
          title: string;
          description: string;
        }[],
      },
      validationSchema: Yup.object({
        title: Yup.string().required("Vakansiyanın başlığı tələb olunur"),
        description: Yup.string().required("Vakansiyanın mətni tələb olunur"),
        image: Yup.string().required("Vakansiyanın şəkili tələb olunur"),
      }),
      onSubmit: async (values) => {
        const azTranslation = {
          lang_code: "AZ",
          title: values.title,
          description: values.description,
        };
        const updatedTranslations = [
          azTranslation,
          ...values.translations.filter((t) => t.lang_code !== "AZ"),
        ];
        const finalValues = {
          ...values,
          translations: updatedTranslations,
        };
        if (isEditing && editingRecord) {
          const isEqual =
            editingRecord.title === values.title &&
            editingRecord.description === values.description &&
            editingRecord.image === values.image &&
            JSON.stringify(editingRecord.translations) ===
              JSON.stringify(values.translations);
          if (isEqual) {
            message.info("Dəyişiklik edilmədi.");
          } else {
            if (editingRecord.image === values.image) {
              const payload = {
                title: values.title,
                description: values.description,
                translations: updatedTranslations,
              };
              const response = await VacanciesService.updateVacancy({
                ...payload,
                id: editingRecord.id,
              });
              if (response.status === 200) {
                message.success("Vakansiya uğurla yeniləndi!");
                await handleAfterSubmit();
              } else {
                message.error(
                  response.errorMessage || "Vakansiyanı yeniləmək alınmadı."
                );
              }
            } else {
              const response = await VacanciesService.updateVacancy({
                ...finalValues,
                id: editingRecord.id,
              });
              if (response.status === 200) {
                message.success("Vakansiya uğurla yeniləndi!");
                await handleAfterSubmit();
              } else {
                message.error(
                  response.errorMessage || "Vakansiyanı yeniləmək alınmadı."
                );
              }
            }
          }
        } else {
          const response = await VacanciesService.addVacancy(finalValues);
          if (response.status === 200) {
            message.success("Vakansiya uğurla əlavə edildi!");
            await handleAfterSubmit();
          } else {
            message.error(
              response.errorMessage || "Vakansiya əlavə etmək alınmadı."
            );
          }
        }
        setLoading(false);
      },
    });
  const handleLanguageButtonClick = (lang: string) => {
    setLangCode(lang);
    const existingTranslation = values.translations.find(
      (t) => t.lang_code === lang
    );

    if (!existingTranslation && lang !== "AZ") {
      setFieldValue("translations", [...values.translations]);
    }
    setEditorState((prev) => ({
      ...prev,
      [lang]: prev[lang] || RichTextEditor.createEmptyValue(),
    }));
  };

  const handleTranslationChange = (value: string, field: string) => {
    if (langCode === "AZ") {
      if (field === "title") {
        setFieldValue("title", value);
      } else if (field === "description") {
        setFieldValue("description", value);
      }
    } else {
      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.lang_code === langCode
      );
      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          [field]: value,
        };
      } else {
        updatedTranslations.push({
          lang_code: langCode,
          title: field === "title" ? value : "",
          description: field === "description" ? value : "",
        });
      }
      setFieldValue("translations", updatedTranslations);
    }
  };
  const handleDeleteClick = async (record: VacancyDataType) => {
    try {
      await VacanciesService.deleteVacancy(record.id.toString());
      setVacancy((prevData) =>
        prevData.filter((item) => item.id !== record.id)
      );
      message.success("Vakansiya uğurla silindi!");
    } catch (error) {
      message.error("Vakansiyanı silmək alınmadı.");
    }
  };
  const columns: TableProps<VacancyDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.key - b.key,
    },
    {
      title: "Vakansiyanın başlığı",
      dataIndex: "title",
      sorter: (a, b) => a.title.localeCompare(b.title),
    },
    {
      title: "Vakansiyanın təsviri",
      dataIndex: "description",
      render: (_, record) => (
        <div
          dangerouslySetInnerHTML={{
            __html:
              record?.description.length > 25
                ? record.description.substring(0, 25) + "..."
                : record.description,
          }}
        />
      ),
    },
    {
      title: "Vakansiyanın şəkli",
      dataIndex: "image",
      render: (_, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedImage(record.image);
            setShowImageModal(true);
          }}
        >
          {record.image ? (
            <img
              style={{ width: "40px" }}
              src={record.image}
              alt={`${record.title.split(" ", 1)}`}
            />
          ) : null}
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu səhifəni siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];
  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setLangCode("AZ");
    setFieldValue("image", null);
    setLogoFileList([]);
    setFieldValue("description", "");
    setEditorState({ AZ: RichTextEditor.createEmptyValue() });
  };
  const handleEditClick = (record: VacancyDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("title", record.title);
    setFieldValue("description", record.description);
    setFieldValue("image", record.image);
    setFieldValue("translations", record.translations);
    setLangCode("AZ");
    setLogoFileList(
      record.image
        ? [
            {
              uid: "-1",
              name: `${record.title} image`,
              status: "done",
              url: record.image,
            },
          ]
        : []
    );
    const initialEditorState: { [key: string]: any } = {
      AZ: editingRecord?.description
        ? RichTextEditor.createValueFromString(
            editingRecord?.description,
            "html"
          )
        : RichTextEditor.createEmptyValue(),
    };
    editingRecord?.translations?.forEach((translation) => {
      initialEditorState[translation.lang_code] = translation.description
        ? RichTextEditor.createValueFromString(translation.description, "html")
        : RichTextEditor.createEmptyValue();
    });

    setEditorState(initialEditorState);
  };
  return (
    <div id="pages">
      <Helmet>
        <title>1412 | Vakansiyalar</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Vakansiyalar</h3>
          </div>

          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<VacancyDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={vacancy}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={
          isEditing ? "Vakansiyanı redaktə edin" : "Yeni Vakansiya əlavə edin"
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditorState({ [langCode]: RichTextEditor.createEmptyValue() });
        }}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <div className="langs">
            {langs?.map((lang, index) => {
              return (
                <div key={index}>
                  <button
                    className={lang.code === langCode ? "active_lang_btn" : ""}
                    type="button"
                    onClick={() => handleLanguageButtonClick(lang.code)}
                  >
                    {lang.name}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="form_input">
            <label className="label" htmlFor="title">
              Vakansiyanın başlığı {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="title"
                id="title"
                placeholder="Vakansiyanın başlığı"
                value={
                  langCode === "AZ"
                    ? values.title
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.title || ""
                }
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "title")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.title} touched={touched.title} />
          </div>
          <div className="form_input">
            <label className="ck_label" htmlFor="description">
              Vakansiyanın təsviri {langCode}
            </label>
            <RichTextEditor
              value={editorState[langCode] || RichTextEditor.createEmptyValue()}
              onChange={handleEditorChange}
            />
            <ErrorMessage
              error={errors.description}
              touched={touched.description}
            />
          </div>
          <div className="form_input">
            <SelectImage
              image_name="Şəkil seçin"
              logoFileList={logoFileList}
              setLogoFileList={setLogoFileList}
              field="image"
              setFieldValue={setFieldValue}
            />
            <ErrorMessage error={errors.image} touched={touched.image} />
          </div>
          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};

export default Vacancy;
